import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Coffee, DollarSign, Clock, ChefHat, Plus, Minus, Star, Flame, Settings } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { dataService, MenuItem, RawMaterial, AddOnCategory, MenuItemAddOns } from '../../services/dataService';
import { useCategories } from '../../hooks/useCategories';
import { deleteMenuItemImageByUrl, uploadMenuItemImage, validateMenuItemImage } from '../../services/menuItemImageService';

interface EditMenuItemModalProps {
  menuItem: MenuItem;
  onClose: () => void;
  onSuccess: () => void;
}

const EditMenuItemModal: React.FC<EditMenuItemModalProps> = ({ menuItem, onClose, onSuccess }) => {
  const { tenantData } = useAuth();
  const { getCategoryNames } = useCategories('menuItem');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rawMaterials, setRawMaterials] = useState<RawMaterial[]>([]);

  // Add-ons configuration state
  const [addOnCategories, setAddOnCategories] = useState<AddOnCategory[]>([]);
  const [selectedAddOnCategories, setSelectedAddOnCategories] = useState<Array<{
    categoryId: string;
    categoryName: string;
    isRequired: boolean;
    minSelections: number;
    maxSelections: number;
    displayOrder: number;
  }>>([]);
  const [loadingAddOns, setLoadingAddOns] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(menuItem.image || '');
  const [removeExistingImage, setRemoveExistingImage] = useState(false);

  const [formData, setFormData] = useState({
    name: menuItem.name || '',
    category: menuItem.category || '',
    price: menuItem.price || 0,
    description: menuItem.description || '',
    preparationTime: menuItem.preparationTime || 0,
    isAvailable: menuItem.isAvailable ?? true,
    autoDeductInventory: menuItem.autoDeductInventory ?? true,
    saleUnit: menuItem.saleUnit || 'piece',
    saleUnitConversion: menuItem.saleUnitConversion || 1,
    ingredients: menuItem.ingredients || [],
    isFeatured: menuItem.isFeatured || false,
    isSpecialOffer: menuItem.isSpecialOffer || false,
    isTodaysSpecial: menuItem.isTodaysSpecial || false,
    originalPrice: menuItem.originalPrice || 0,
    spiceLevel: (menuItem.spiceLevel || 0) as 0 | 1 | 2 | 3 | 4 | 5,
    dietaryInfo: menuItem.dietaryInfo || [] as string[],
    badges: menuItem.badges || [] as string[],
    tags: menuItem.tags || [] as string[]
  });

  const categories = getCategoryNames();

  const loadRawMaterials = useCallback(async () => {
    try {
      const materials = await dataService.getRawMaterials(tenantData!.id);
      setRawMaterials(materials);
    } catch (error) {
      console.error('Error loading raw materials:', error);
    }
  }, [tenantData]);

  useEffect(() => {
    if (tenantData?.id) {
      loadRawMaterials();
      loadAddOnsConfiguration();
    }
  }, [tenantData, loadRawMaterials]);

  useEffect(() => {
    return () => {
      if (imagePreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
    };
  }, [imagePreviewUrl]);

  const loadAddOnsConfiguration = useCallback(async () => {
    try {
      setLoadingAddOns(true);
      
      // Load all available add-on categories
      const categories = await dataService.getAddOnCategories(tenantData!.id);
      setAddOnCategories(categories);
      
      // Load existing add-ons configuration for this menu item
      const existingConfig = await dataService.getMenuItemAddOns(tenantData!.id, menuItem.id!);
      if (existingConfig && existingConfig.addOnCategories) {
        setSelectedAddOnCategories(existingConfig.addOnCategories);
      }
    } catch (error) {
      console.error('Error loading add-ons configuration:', error);
    } finally {
      setLoadingAddOns(false);
    }
  }, [tenantData, menuItem.id]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type, checked } = e.target as HTMLInputElement;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? parseFloat(value) || 0 : value
    }));
  };

  const addIngredient = () => {
    setFormData(prev => ({
      ...prev,
      ingredients: [
        ...prev.ingredients,
        {
          rawMaterialId: '',
          quantity: 1,
          unit: ''
        }
      ]
    }));
  };

  const removeIngredient = (index: number) => {
    setFormData(prev => ({
      ...prev,
      ingredients: prev.ingredients.filter((_, i) => i !== index)
    }));
  };

  const updateIngredient = (index: number, field: string, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      ingredients: prev.ingredients.map((ingredient, i) => 
        i === index ? { ...ingredient, [field]: value } : ingredient
      )
    }));
  };

  const getRawMaterialName = (materialId: string) => {
    const material = rawMaterials.find(m => m.id === materialId);
    return material ? material.name : 'Unknown Material';
  };

  const getRawMaterialUnit = (materialId: string) => {
    const material = rawMaterials.find(m => m.id === materialId);
    return material ? (material.subUnit || material.baseUnit) : '';
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    const validationError = validateMenuItemImage(file);
    if (validationError) {
      setError(validationError);
      e.target.value = '';
      return;
    }

    if (imagePreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreviewUrl);
    }

    setError('');
    setImageFile(file);
    setRemoveExistingImage(false);
    setImagePreviewUrl(URL.createObjectURL(file));
  };

  const clearImageSelection = () => {
    if (imagePreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreviewUrl);
    }
    setImageFile(null);
    setImagePreviewUrl('');
    setRemoveExistingImage(!!menuItem.image);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Please enter menu item name');
      return;
    }

    if (!formData.category) {
      setError('Please select a category');
      return;
    }

    if (formData.price <= 0) {
      setError('Price must be greater than 0');
      return;
    }

    if (formData.preparationTime < 0) {
      setError('Preparation time cannot be negative');
      return;
    }

    // Validate ingredients
    for (let i = 0; i < formData.ingredients.length; i++) {
      const ingredient = formData.ingredients[i];
      if (!ingredient.rawMaterialId) {
        setError(`Please select a raw material for ingredient ${i + 1}`);
        return;
      }
      if (ingredient.quantity <= 0) {
        setError(`Quantity for ingredient ${i + 1} must be greater than 0`);
        return;
      }
    }

    try {
      setLoading(true);
      setError('');

      // Prepare ingredients with proper units
      const processedIngredients = formData.ingredients.map(ingredient => ({
        ...ingredient,
        unit: getRawMaterialUnit(ingredient.rawMaterialId)
      }));

      const updateData: Partial<MenuItem> = {
        name: formData.name.trim(),
        category: formData.category,
        price: formData.price,
        description: formData.description.trim() || undefined,
        preparationTime: formData.preparationTime,
        isAvailable: formData.isAvailable,
        autoDeductInventory: formData.autoDeductInventory,
        saleUnit: formData.saleUnit,
        saleUnitConversion: formData.saleUnitConversion,
        ingredients: processedIngredients,
        isFeatured: formData.isFeatured,
        isSpecialOffer: formData.isSpecialOffer,
        isTodaysSpecial: formData.isTodaysSpecial,
        originalPrice: formData.isSpecialOffer && formData.originalPrice > 0 ? formData.originalPrice : undefined,
        spiceLevel: formData.spiceLevel > 0 ? (formData.spiceLevel as 1 | 2 | 3 | 4 | 5) : undefined,
        dietaryInfo: formData.dietaryInfo.length > 0 ? formData.dietaryInfo : undefined,
        badges: formData.badges.length > 0 ? formData.badges : undefined,
        tags: formData.tags.length > 0 ? formData.tags : undefined
      };

      // Filter out undefined values to avoid Firebase errors
      const cleanUpdateData = Object.entries(updateData).reduce((acc, [key, value]) => {
        if (value !== undefined) {
          (acc as any)[key] = value;
        }
        return acc;
      }, {} as Partial<MenuItem>);

      if (imageFile) {
        const imageUrl = await uploadMenuItemImage(tenantData!.id, menuItem.id!, imageFile);
        cleanUpdateData.image = imageUrl;
      } else if (removeExistingImage) {
        cleanUpdateData.image = '';
      }

      await dataService.update<MenuItem>(tenantData!.id, 'menuItems', menuItem.id!, cleanUpdateData);

      if (imageFile || removeExistingImage) {
        await deleteMenuItemImageByUrl(menuItem.image);
      }
      
      // Update add-ons configuration
      try {
        await dataService.configureMenuItemAddOns(tenantData!.id, menuItem.id!, selectedAddOnCategories);
      } catch (error) {
        console.error('Error updating add-ons configuration:', error);
        // Don't fail the entire operation if add-ons configuration fails
      }
      
      onSuccess();
    } catch (error) {
      console.error('Error updating menu item:', error);
      setError('Failed to update menu item. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const modalContent = (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-y-auto border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl tablet:text-2xl font-bold text-gray-900 dark:text-white">
            Edit Menu Item
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
              <p className="text-red-700 dark:text-red-300">{error}</p>
            </div>
          )}

          <div className="grid grid-cols-1 tablet:grid-cols-2 gap-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Basic Information</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Item Name *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Coffee className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  </div>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    className="input-field pl-10"
                    placeholder="Enter item name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Category *
                </label>
                <select
                  name="category"
                  required
                  value={formData.category}
                  onChange={handleInputChange}
                  className="input-field"
                >
                  <option value="">Select category</option>
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Price *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <DollarSign className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  </div>
                  <input
                    type="number"
                    name="price"
                    required
                    min="0"
                    step="0.01"
                    value={formData.price}
                    onChange={handleInputChange}
                    className="input-field pl-10"
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Preparation Time (minutes)
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Clock className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  </div>
                  <input
                    type="number"
                    name="preparationTime"
                    min="0"
                    value={formData.preparationTime}
                    onChange={handleInputChange}
                    className="input-field pl-10"
                    placeholder="5"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  className="input-field"
                  rows={3}
                  placeholder="Enter item description"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Item Image
                </label>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleImageChange}
                  className="input-field"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Upload JPG, PNG, or WEBP up to 5MB.
                </p>
                {imagePreviewUrl && (
                  <div className="mt-3 flex items-center gap-3">
                    <img
                      src={imagePreviewUrl}
                      alt="Menu item preview"
                      className="w-20 h-20 object-cover rounded-lg border border-gray-200 dark:border-gray-700"
                    />
                    <button
                      type="button"
                      onClick={clearImageSelection}
                      className="text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                    >
                      Remove image
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Availability & Features */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Availability & Features</h3>
              
              <div className="space-y-3">
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    name="isAvailable"
                    checked={formData.isAvailable}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Available for ordering</span>
                </label>

                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    name="autoDeductInventory"
                    checked={formData.autoDeductInventory}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Auto-deduct inventory when sold</span>
                </label>

                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    name="isFeatured"
                    checked={formData.isFeatured}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Featured item</span>
                </label>

                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    name="isSpecialOffer"
                    checked={formData.isSpecialOffer}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Special offer</span>
                </label>

                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    name="isTodaysSpecial"
                    checked={formData.isTodaysSpecial}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Today's special</span>
                </label>
              </div>

              {formData.isSpecialOffer && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Original Price
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <DollarSign className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                    </div>
                    <input
                      type="number"
                      name="originalPrice"
                      min="0"
                      step="0.01"
                      value={formData.originalPrice}
                      onChange={handleInputChange}
                      className="input-field pl-10"
                      placeholder="Original price before discount"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Spice Level
                </label>
                <div className="flex items-center space-x-2">
                  {[0, 1, 2, 3, 4, 5].map(level => (
                    <button
                      key={level}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, spiceLevel: level as 0 | 1 | 2 | 3 | 4 | 5 }))}
                      className={`p-2 rounded-lg transition-colors ${
                        formData.spiceLevel === level
                          ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      {level === 0 ? 'None' : Array(level).fill(<Flame className="w-4 h-4" />)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Ingredients Section */}
          <div className="mt-8 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Ingredients</h3>
              <button
                type="button"
                onClick={addIngredient}
                className="btn-secondary text-sm"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Ingredient
              </button>
            </div>

            {formData.ingredients.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <ChefHat className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-2" />
                <p className="text-gray-600 dark:text-gray-300">No ingredients added yet</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Add ingredients to track inventory usage</p>
              </div>
            ) : (
              <div className="space-y-3">
                {formData.ingredients.map((ingredient, index) => {
                  const selectedMaterial = rawMaterials.find(m => m.id === ingredient.rawMaterialId);
                  
                  return (
                    <div key={index} className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                      {/* Material Selection */}
                      <div className="grid grid-cols-1 tablet:grid-cols-3 gap-3 mb-3">
                        <div className="tablet:col-span-2">
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Raw Material
                          </label>
                          <select
                            value={ingredient.rawMaterialId}
                            onChange={(e) => {
                              const selectedMaterial = rawMaterials.find(m => m.id === e.target.value);
                              updateIngredient(index, 'rawMaterialId', e.target.value);
                              if (selectedMaterial) {
                                // Default to sub-unit for recipes
                                updateIngredient(index, 'unit', selectedMaterial.subUnit || selectedMaterial.baseUnit);
                              }
                            }}
                            className="input-field text-sm"
                          >
                            <option value="">Select material</option>
                            {rawMaterials.map(material => (
                              <option key={material.id} value={material.id}>
                                {material.name} ({material.subUnit || material.baseUnit})
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Quantity
                          </label>
                          <input
                            type="number"
                            min="0.01"
                            step="0.01"
                            value={ingredient.quantity}
                            onChange={(e) => updateIngredient(index, 'quantity', parseFloat(e.target.value) || 0)}
                            className="input-field text-sm"
                            placeholder="1"
                          />
                        </div>
                      </div>

                      {/* Unit Display */}
                      {selectedMaterial && (
                        <div className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                          Unit: {selectedMaterial.subUnit || selectedMaterial.baseUnit}
                        </div>
                      )}

                      {/* Remove Button */}
                      <button
                        type="button"
                        onClick={() => removeIngredient(index)}
                        className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 text-sm flex items-center"
                      >
                        <Minus className="w-3 h-3 mr-1" />
                        Remove
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Add-Ons Configuration */}
          <div className="space-y-4 pt-6 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-2">
              <Settings className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Add-Ons Configuration</h3>
            </div>
            
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Configure which add-on categories customers can select from when ordering this item.
            </div>

            {loadingAddOns ? (
              <div className="text-center py-6">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-gray-600 dark:text-gray-400 mt-2">Loading add-ons...</p>
              </div>
            ) : addOnCategories.length === 0 ? (
              <div className="text-center py-6 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <Settings className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-3" />
                <p className="text-gray-600 dark:text-gray-400 mb-2">No add-on categories available</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Create add-on categories first in the Add-On Categories section
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {addOnCategories.map((category) => {
                  const isSelected = selectedAddOnCategories.some(selected => selected.categoryId === category.id);
                  const selectedCategory = selectedAddOnCategories.find(selected => selected.categoryId === category.id);
                  
                  return (
                    <div key={category.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <input
                          type="checkbox"
                          id={`edit-category-${category.id}`}
                          checked={isSelected}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedAddOnCategories(prev => [...prev, {
                                categoryId: category.id!,
                                categoryName: category.name,
                                isRequired: category.isRequired || false,
                                minSelections: category.minSelections || 0,
                                maxSelections: category.maxSelections || 0,
                                displayOrder: category.displayOrder || 0
                              }]);
                            } else {
                              setSelectedAddOnCategories(prev => prev.filter(cat => cat.categoryId !== category.id));
                            }
                          }}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded mt-1"
                        />
                        
                        <div className="flex-1">
                          <label htmlFor={`edit-category-${category.id}`} className="flex items-center space-x-2 cursor-pointer">
                            <span className="font-medium text-gray-900 dark:text-white">{category.name}</span>
                            {category.isRequired && (
                              <span className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 text-xs rounded-full">
                                Required
                              </span>
                            )}
                          </label>
                          
                          {category.description && (
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{category.description}</p>
                          )}
                          
                          <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500 dark:text-gray-400">
                            <span>Min: {category.minSelections}</span>
                            <span>Max: {category.maxSelections === 0 ? 'Unlimited' : category.maxSelections}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Category-specific settings when selected */}
                      {isSelected && selectedCategory && (
                        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 space-y-3">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Required for this item
                              </label>
                              <input
                                type="checkbox"
                                checked={selectedCategory.isRequired}
                                onChange={(e) => {
                                  setSelectedAddOnCategories(prev => prev.map(cat => 
                                    cat.categoryId === category.id 
                                      ? { ...cat, isRequired: e.target.checked }
                                      : cat
                                  ));
                                }}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Min Selections
                              </label>
                              <input
                                type="number"
                                min="0"
                                max={category.maxSelections || 999}
                                value={selectedCategory.minSelections}
                                onChange={(e) => {
                                  setSelectedAddOnCategories(prev => prev.map(cat => 
                                    cat.categoryId === category.id 
                                      ? { ...cat, minSelections: parseInt(e.target.value) || 0 }
                                      : cat
                                  ));
                                }}
                                className="input-field text-sm"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Max Selections
                              </label>
                              <input
                                type="number"
                                min={selectedCategory.minSelections}
                                value={selectedCategory.maxSelections}
                                onChange={(e) => {
                                  setSelectedAddOnCategories(prev => prev.map(cat => 
                                    cat.categoryId === category.id 
                                      ? { ...cat, maxSelections: parseInt(e.target.value) || 0 }
                                      : cat
                                  ));
                                }}
                                className="input-field text-sm"
                              />
                            </div>
                          </div>
                          
                          <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Display Order
                            </label>
                            <input
                              type="number"
                              min="0"
                              value={selectedCategory.displayOrder}
                              onChange={(e) => {
                                setSelectedAddOnCategories(prev => prev.map(cat => 
                                  cat.categoryId === category.id 
                                    ? { ...cat, displayOrder: parseInt(e.target.value) || 0 }
                                    : cat
                                ));
                              }}
                              className="input-field text-sm"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-4 pt-6 border-t border-gray-200 dark:border-gray-700 mt-8">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Updating...' : 'Update Menu Item'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default EditMenuItemModal; 